/**
 * Per-form data explorer: what has actually been ingested for one form.
 *
 * Four panes over three existing tables. ENV is chosen explicitly and
 * never defaulted silently -- Priority re-issues the same key values in
 * every company, so an implicit ENV shows one company's rows under
 * another's heading.
 *
 * Exposed as window.FormDataTab; soc-app.jsx renders it and nothing else
 * from this file.
 */
const { useState, useEffect } = React;

const fdCls = (...xs) => xs.filter(Boolean).join(' ');
const fdTime = (iso) => (iso ? iso.replace('T', ' ').slice(0, 19) : '—');

function RunPane({ run, lang }) {
  if (!run) {
    return (
      <div className="card fd-empty">
        {lang === 'he'
          ? 'טרם רצה גיבוי מלא לטופס זה בחברה זו.'
          : 'No backfill run recorded for this form in this company.'}
      </div>
    );
  }
  const stale = run.windows_unloaded > 0;
  const cells = [
    ['status', run.status],
    ['win_col', run.win_col || '— (paged)'],
    ['key range', `${run.key_min ?? '—'} … ${run.key_max ?? '—'}`],
    ['resume_lo', run.resume_lo ?? '—'],
    ['windows done', run.windows_done],
    ['rows total', run.rows_total],
    ['windows unloaded', run.windows_unloaded],
    ['attempts', run.attempts],
    ['updated', fdTime(run.updated_at)],
  ];
  return (
    <div className="card fd-run">
      <div className="fd-kv">
        {cells.map(([k, v]) => (
          <div className="fd-kv-row" key={k}>
            <span className="fd-k">{k}</span>
            <span className="fd-v mono" dir="auto">{String(v)}</span>
          </div>
        ))}
      </div>
      {stale && (
        <div className="fd-warn">
          {lang === 'he'
            ? `${run.windows_unloaded} חלונות נחתו אך לא נטענו — המראה אינה שלמה.`
            : `${run.windows_unloaded} window(s) landed but were never typed-loaded — the mirror is NOT complete.`}
        </div>
      )}
      {run.detail && <pre className="fd-detail" dir="auto">{run.detail}</pre>}
    </div>
  );
}

function WindowsPane({ ename, env, lang, onPick }) {
  const [state, setState] = useState({ loading: true });
  const [kind, setKind] = useState('');
  useEffect(() => {
    setState({ loading: true });
    PCMAdapter.getFormWindows(ename, { env, kind })
      .then((d) => setState({ loading: false, data: d }))
      .catch((e) => setState({ loading: false, error: String(e.message || e) }));
  }, [ename, env, kind]);

  // Rendered above every branch below (loading excepted) so the filter
  // itself is never the thing that strands the operator -- clicking
  // `formdump` on a form with none used to make both the buttons and the
  // way back to `all` disappear along with the (falsely-worded) empty state.
  const filterBar = (
    <div className="fd-filter">
      {['', 'rows', 'log', 'formdump'].map((k) => (
        <button key={k || 'all'} className={fdCls('tab', kind === k && 'active')}
                onClick={() => setKind(k)}>{k || 'all'}</button>
      ))}
    </div>
  );

  if (state.loading) return <div className="card">{lang === 'he' ? 'טוען…' : 'Loading…'}</div>;
  if (state.error) {
    return (
      <div className="card fd-empty">
        {filterBar}
        <div dir="auto">{state.error}</div>
      </div>
    );
  }
  const { data } = state;
  if (data.total === 0) {
    return (
      <div className="card fd-empty">
        {filterBar}
        {kind
          ? (lang === 'he'
              ? `לא התקבלו חלונות מסוג ${kind} עבור ${ename} בחברה ${env}.`
              : `No ${kind} windows received for ${ename} in ${env}.`)
          : (lang === 'he'
              ? `לא התקבלו חלונות עבור ${ename} בחברה ${env}.`
              : `No windows received for ${ename} in ${env}.`)}
      </div>
    );
  }
  return (
    <div className="card">
      {filterBar}
      <div className="tbl-wrap" tabIndex={0}>
        <table className="tbl">
          <thead>
            <tr>
              <th>#</th><th>kind</th><th>lo</th><th>hi</th>
              <th>declared</th><th>counted</th><th>cols</th>
              <th>received</th><th>loaded</th>
            </tr>
          </thead>
          <tbody>
            {/* row-clickable is the repo's own affordance class;
                tbl-clickable is not. */}
            {data.items.map((w) => (
              <tr key={w.id} onClick={() => onPick(w)}
                  className={fdCls(
                    'row-clickable',
                    w.rows_declared !== null && w.rows_declared !== w.rows_counted && 'row-warn',
                  )}>
                <td className="mono">{w.batch_num}</td>
                <td className="mono">{w.kind}</td>
                <td className="mono">{w.lo}</td>
                <td className="mono">{w.hi ?? '—'}</td>
                <td className="mono">{w.rows_declared ?? '—'}</td>
                <td className="mono">{w.rows_counted}</td>
                <td className="mono">{w.cols}</td>
                <td className="mono" dir="ltr">{fdTime(w.received_at)}</td>
                <td className="mono" dir="ltr">{w.loaded_at ? fdTime(w.loaded_at) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="fd-note">
        {lang === 'he'
          ? `${data.items.length} מתוך ${data.total}`
          : `showing ${data.items.length} of ${data.total}`}
      </div>
    </div>
  );
}

function RawPane({ ename, batch, lang, onClose }) {
  const [body, setBody] = useState(null);
  useEffect(() => {
    setBody(null);
    PCMAdapter.getWindowRaw(ename, batch.id)
      .then(setBody)
      .catch((e) => setBody({ available: false, reason: (e && e.detail) || 'request failed', text: '' }));
  }, [ename, batch.id]);

  return (
    <div className="card fd-raw">
      <div className="fd-raw-head">
        <span className="mono">
          {batch.kind} · lo={batch.lo} · hi={batch.hi ?? '—'}
        </span>
        <button className="tab" onClick={onClose}>{lang === 'he' ? 'סגור' : 'close'}</button>
      </div>
      {!body && <div>{lang === 'he' ? 'טוען…' : 'Loading…'}</div>}
      {body && !body.available && (
        <div className="fd-empty">
          {body.reason === 'released on load'
            ? (lang === 'he'
                ? 'המטען שוחרר כשהחלון נטען. הנתונים נמצאים בלשונית השורות.'
                : 'Payload released when this window was typed-loaded. The data is in the Rows pane.')
            : `${lang === 'he' ? 'אין מטען' : 'No payload'} — ${body.reason}`}
        </div>
      )}
      {body && body.available && (
        <pre className="fd-raw-text" dir="auto">{body.text}</pre>
      )}
    </div>
  );
}

function RowsPane({ ename, env, lang }) {
  const [state, setState] = useState({ loading: true });
  const [cursor, setCursor] = useState(null);
  useEffect(() => {
    setState({ loading: true });
    PCMAdapter.getFormRows(ename, { env, after: cursor })
      .then((d) => setState({ loading: false, data: d }))
      .catch((e) => setState({ loading: false, error: String(e.message || e) }));
  }, [ename, env, cursor]);

  if (state.loading) return <div className="card">{lang === 'he' ? 'טוען…' : 'Loading…'}</div>;
  if (state.error) {
    return <div className="card fd-empty" dir="auto">{state.error}</div>;
  }
  const { columns, rows, next_after: next, hidden_columns: hidden = [] } = state.data;
  // Credential columns are dropped server-side for a tenant login and shown
  // only to the admin; saying WHICH ones keeps the pane honest about it.
  const hiddenNote = hidden.length > 0 && (
    <p className="fd-note" dir="auto">
      {lang === 'he'
        ? `${hidden.length} עמודות אישורים מוסתרות: ${hidden.join(', ')}`
        : `${hidden.length} credential column(s) hidden: ${hidden.join(', ')}`}
    </p>
  );
  // The pager renders even on an empty page. next_after is non-null
  // whenever a page came back full, so a table whose row count is an exact
  // multiple of the limit yields one trailing empty page -- without a
  // `first` button there the operator has no way back but leaving the tab.
  const pager = (
    <div className="fd-pager">
      <button className="tab" disabled={!cursor} onClick={() => setCursor(null)}>
        {lang === 'he' ? 'התחלה' : 'first'}
      </button>
      <button className="tab" disabled={!next} onClick={() => setCursor(next)}>
        {lang === 'he' ? 'הבא' : 'next'}
      </button>
    </div>
  );
  if (rows.length === 0) {
    return (
      <div className="card fd-empty">
        {lang === 'he' ? 'אין שורות ממופות.' : 'No mirrored rows.'}
        {pager}
      </div>
    );
  }
  return (
    <div className="card">
      {hiddenNote}
      <div className="tbl-wrap" tabIndex={0}>
        <table className="tbl">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.name}>
                  {/* T_ table dictionaries carry no column titles, so the
                      fallback IS the name -- printing it twice is noise. */}
                  <div dir="auto" className={c.title ? undefined : 'mono'}>{c.title || c.name}</div>
                  {c.title && <div className="fd-colname mono">{c.name}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.name} dir="auto">
                    {r[c.name] === null || r[c.name] === undefined ? '' : String(r[c.name])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pager}
    </div>
  );
}

// `ename` is the MIRROR SOURCE -- `T_<TNAME>` -- not the screen the user
// clicked. `formEname` is that screen, and is null when the two are the
// same (a table dictionary opened directly from the Tables tab).
function FormDataTab({ ename, formEname, lang }) {
  // { loading } | { loading: false, envs } | { loading: false, error }.
  // A failed request must not collapse into the same shape as a genuinely
  // empty envs list -- "the request failed" is not "the data never came".
  const [envsState, setEnvsState] = useState({ loading: true });
  const [env, setEnv] = useState('');
  const [pane, setPane] = useState('windows');
  const [runState, setRunState] = useState({ run: null });
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    setEnvsState({ loading: true }); setEnv(''); setPicked(null);
    PCMAdapter.getFormEnvs(ename)
      .then((d) => {
        setEnvsState({ loading: false, envs: d.envs });
        if (d.envs.length > 0) setEnv(d.envs[0].env);
      })
      .catch((e) => setEnvsState({ loading: false, error: String(e.message || e) }));
  }, [ename]);

  useEffect(() => {
    if (!env) { setRunState({ run: null }); return; }
    PCMAdapter.getFormRun(ename, env)
      .then((d) => setRunState({ run: d.run }))
      .catch((e) => setRunState({ run: null, error: String(e.message || e) }));
  }, [ename, env]);

  if (envsState.loading) return <div className="card">{lang === 'he' ? 'טוען…' : 'Loading…'}</div>;
  if (envsState.error) {
    return <div className="card fd-empty" dir="auto">{envsState.error}</div>;
  }
  const { envs } = envsState;
  const forForm = formEname && formEname !== ename;
  if (envs.length === 0) {
    return (
      <div className="card fd-empty">
        <div dir="auto">
          {lang === 'he'
            ? `הטבלה ${ename} טרם מוראה.`
            : `The table ${ename} has not been mirrored yet.`}
        </div>
        {forForm && (
          <div className="fd-src" dir="auto">
            {lang === 'he'
              ? `זו טבלת הבסיס של ${formEname}. הטופס המורכב נמצא בלשונית "תצוגה".`
              : `That is the base table of ${formEname}. The assembled form is under View.`}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fd">
      {forForm && (
        <div className="fd-src" dir="auto">
          {lang === 'he'
            ? <>מקור: <span className="mono" dir="ltr">{ename}</span> — טבלת הבסיס של {formEname}. הטופס המורכב, על כל הצירופים שלו, נמצא בלשונית "תצוגה".</>
            : <>Mirror table <span className="mono" dir="ltr">{ename}</span> — the base table of {formEname}. The assembled form, with its lookups, is under View.</>}
        </div>
      )}
      <div className="fd-head">
        <label className="fd-env">
          <span>{lang === 'he' ? 'חברה' : 'Company (ENV)'}</span>
          <select value={env} onChange={(e) => { setEnv(e.target.value); setPicked(null); }}
                  disabled={envs.length === 1}>
            {envs.map((e) => (
              <option key={e.env} value={e.env}>
                {`${e.env} — ${e.windows} win / ${e.rows_total} rows`}
              </option>
            ))}
          </select>
        </label>
        <div className="tabs">
          {['windows', 'run', 'rows'].map((p) => (
            <button key={p} className={fdCls('tab', pane === p && 'active')}
                    onClick={() => setPane(p)}>{p}</button>
          ))}
        </div>
      </div>

      {pane === 'run' && (
        runState.error
          ? <div className="card fd-empty" dir="auto">{runState.error}</div>
          : <RunPane run={runState.run} lang={lang}/>
      )}
      {pane === 'windows' && (
        <>
          <WindowsPane ename={ename} env={env} lang={lang} onPick={setPicked}/>
          {picked && (
            <RawPane ename={ename} batch={picked} lang={lang}
                     onClose={() => setPicked(null)}/>
          )}
        </>
      )}
      {pane === 'rows' && <RowsPane key={env} ename={ename} env={env} lang={lang}/>}
    </div>
  );
}

Object.assign(window, { FormDataTab });
